import React, { useState, useMemo, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, TextField, Button, IconButton,
    Chip, CircularProgress, Alert,
} from '@mui/material';
import CloseIcon    from '@mui/icons-material/Close';
import AddLinkIcon  from '@mui/icons-material/AddLink';
import reportServices from '../../../react-query/services/reportServices';
import { displayPlatformName } from '../../common/displayPlatformName';

// Canonical dedupe key for a post link — mirrors the backend's extractPostInfo/
// getPostDedupeKey (pluginbackendnode/routes/reports/common.js) so "is this
// already in the report?" agrees with what the server will actually decide.
// Without this, a link pasted as e.g. /reel/ABC/? vs the stored /reel/ABC/
// would look brand-new here but get silently skipped as a duplicate server-side.
function extractDedupeKey(rawUrl) {
    try {
        let trimmed = rawUrl.trim().replace(/\/+$/, '');
        if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) trimmed = `https://${trimmed}`;
        const url = new URL(trimmed);
        const host = url.hostname.toLowerCase();
        const segments = url.pathname.split('/').filter(Boolean);

        if (host.includes('instagram.com')) {
            const typeMap = { reel: true, reels: true, p: true, tv: true, stories: true };
            const normalized = segments.map(s => s.toLowerCase());
            if (normalized[0] === 'share') { normalized.shift(); segments.shift(); }
            const idx = normalized.findIndex(s => typeMap[s]);
            if (idx === -1) return null;
            const shortcode = normalized[idx] === 'stories' ? segments[idx + 2] : segments[idx + 1];
            return shortcode?.trim().toLowerCase() || null;
        }
        if (host.includes('youtube.com') || host.includes('youtu.be')) {
            if (host.includes('youtu.be')) return (segments[0] || '').toLowerCase() || null;
            if (segments[0] === 'shorts') return (segments[1] || '').toLowerCase() || null;
            const videoId = url.searchParams.get('v');
            return videoId ? videoId.toLowerCase() : null;
        }
        // X: /<handle>/status/<id>. The numeric id is the same across x.com / twitter.com forms.
        if (/(^|\.)(x|twitter)\.com$/.test(host)) {
            const statusIdx = segments.findIndex(s => s.toLowerCase() === 'status');
            const id = statusIdx !== -1 ? segments[statusIdx + 1] : null;
            return id && /^\d+$/.test(id) ? id : null;
        }
        // LinkedIn: the activity id inside /posts/<slug>-activity-<id>-<hash> or urn:li:activity:<id>.
        if (host.includes('linkedin.com')) {
            const m = decodeURIComponent(url.pathname).match(/(?:activity|ugcPost|share)[-:](\d{10,})/i);
            return m ? m[1] : null;
        }
        return null;
    } catch {
        return null;
    }
}

const LINK_PLATFORM_LABELS = { instagram: 'Instagram', youtube: 'YouTube', twitter: 'X', linkedin: 'LinkedIn' };
// Report platform name (as stored on the report) -> the link platform it accepts.
const REPORT_PLATFORM_KEYS = { Instagram: 'instagram', YouTube: 'youtube', Twitter: 'twitter', X: 'twitter', LinkedIn: 'linkedin' };

const POST_PLACEHOLDERS = {
    Instagram: 'https://www.instagram.com/reel/...\nhttps://www.instagram.com/p/... 8000',
    YouTube: 'https://www.youtube.com/watch?v=...\nhttps://youtu.be/... 5000',
    Twitter: 'https://x.com/handle/status/...\nhttps://x.com/handle/status/... 5000',
    X: 'https://x.com/handle/status/...\nhttps://x.com/handle/status/... 5000',
    LinkedIn: 'https://www.linkedin.com/posts/...\nhttps://www.linkedin.com/posts/... 5000',
};

// Parse "https://... 8000" or "https://... 8000.50" — budget is the trailing number token
function classifyLine(line, platform) {
    const trimmed = line.trim();
    if (!trimmed) return null;

    let rawUrl = trimmed;
    let budget = null;
    const m = trimmed.match(/^(\S+)\s+(\d+(?:\.\d+)?)$/);
    if (m) {
        const amt = parseFloat(m[2]);
        if (amt > 0) { rawUrl = m[1]; budget = amt; }
    }

    // Which platform this link belongs to (mirrors extractPostInfo on the backend). X and
    // LinkedIn links must actually be post links — a profile URL can't be tracked as a post.
    let linkPlatform = null;
    if (rawUrl.includes('instagram.com')) linkPlatform = 'instagram';
    else if (rawUrl.includes('youtube.com') || rawUrl.includes('youtu.be')) linkPlatform = 'youtube';
    else if (/(^|[/.])(x|twitter)\.com\//i.test(rawUrl)) linkPlatform = 'twitter';
    else if (rawUrl.includes('linkedin.com')) linkPlatform = 'linkedin';
    if (!linkPlatform) return { url: rawUrl, budget, status: 'invalid', reason: 'Unrecognized URL' };
    if ((linkPlatform === 'twitter' || linkPlatform === 'linkedin') && !extractDedupeKey(rawUrl)) {
        return { url: rawUrl, budget, status: 'invalid', reason: `That isn't a ${LINK_PLATFORM_LABELS[linkPlatform]} post link` };
    }
    const reportPlatformKey = REPORT_PLATFORM_KEYS[platform];
    if (reportPlatformKey && linkPlatform !== reportPlatformKey) {
        return {
            url: rawUrl, budget, status: 'invalid',
            reason: `${LINK_PLATFORM_LABELS[linkPlatform]} URL — this report is ${LINK_PLATFORM_LABELS[reportPlatformKey]} only`,
        };
    }
    return { url: rawUrl, budget, status: 'valid' };
}

const fmtBudget = n => `₹${Number.isInteger(n) ? n.toLocaleString('en-IN') : n.toFixed(2)}`;

const AddManualLinksModal = ({ open, onClose, reportId, platform, existingPostCount = 0, existingPosts = [], onSuccess }) => {
    const [rawText, setRawText]       = useState('');
    const [loading, setLoading]       = useState(false);
    const [serverErrors, setServerErrors]         = useState([]);
    const [serverDuplicates, setServerDuplicates] = useState([]);
    const [submitError, setSubmitError]           = useState('');
    const textareaRef = useRef(null);

    const existingKeySet = useMemo(
        () => new Set((existingPosts || []).map(p => extractDedupeKey(p.link)).filter(Boolean)),
        [existingPosts]
    );

    // Character offset of each raw line within rawText — lets a click on a flagged
    // entry select/scroll to that exact line in the textarea, since with hundreds
    // of pasted URLs the flagged text alone doesn't say where to find it.
    const lineOffsets = useMemo(() => {
        const rawLines = rawText.split('\n');
        let pos = 0;
        return rawLines.map(raw => {
            const start = pos;
            pos += raw.length + 1; // +1 for the '\n' consumed by split
            return { start, end: start + raw.length };
        });
    }, [rawText]);

    const lines = useMemo(() => {
        return rawText.split('\n')
            .map((raw, lineIndex) => ({ lineIndex, parsed: classifyLine(raw, platform) }))
            .filter(x => x.parsed)
            .map(({ lineIndex, parsed }) => ({
                ...parsed,
                lineNumber: lineIndex + 1,
                lineIndex,
                ...(parsed.status === 'valid' ? { isExisting: existingKeySet.has(extractDedupeKey(parsed.url)) } : {}),
            }));
    }, [rawText, platform, existingKeySet]);

    const jumpToLine = (lineIndex) => {
        const offset = lineOffsets[lineIndex];
        const el = textareaRef.current;
        if (!offset || !el) return;
        el.focus();
        el.setSelectionRange(offset.start, offset.end);
    };

    const validLines        = lines.filter(l => l.status === 'valid');
    const invalidLines      = lines.filter(l => l.status === 'invalid');
    const newLines          = validLines.filter(l => !l.isExisting);
    const budgetUpdateLines = validLines.filter(l => l.isExisting && l.budget != null);
    const alreadyAddedLines = validLines.filter(l => l.isExisting && l.budget == null);

    const totalAfter      = existingPostCount + newLines.length;
    const overCap         = totalAfter > 1500;
    const submittableCount = newLines.length + budgetUpdateLines.length;
    const canSubmit       = submittableCount > 0 && !overCap && !loading;

    const submitLabel = loading ? 'Saving…'
        : newLines.length > 0 && budgetUpdateLines.length > 0
            ? `Add ${newLines.length} Post${newLines.length !== 1 ? 's' : ''} & Update ${budgetUpdateLines.length} Budget${budgetUpdateLines.length !== 1 ? 's' : ''}`
        : newLines.length > 0
            ? `Add ${newLines.length} Post${newLines.length !== 1 ? 's' : ''}`
        : budgetUpdateLines.length > 0
            ? `Update ${budgetUpdateLines.length} Budget${budgetUpdateLines.length !== 1 ? 's' : ''}`
        : 'Add Posts';

    const handleClose = () => {
        if (loading) return;
        setRawText('');
        setServerErrors([]);
        setServerDuplicates([]);
        setSubmitError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setLoading(true);
        setServerErrors([]);
        setServerDuplicates([]);
        setSubmitError('');
        try {
            const posts = [
                ...newLines.map(l => ({ link: l.url, ...(l.budget != null ? { budget: l.budget } : {}) })),
                ...budgetUpdateLines.map(l => ({ link: l.url, budget: l.budget })),
            ];
            const result = await reportServices.AddPostsByReport({ reportId, posts });
            const bad  = result?.invalidUrls ?? [];
            const dupe = result?.duplicates ?? [];
            if (bad.length > 0 || dupe.length > 0) {
                // Something we expected to add didn't make it in — e.g. someone else
                // added the same post between this modal opening and submit. Keep the
                // modal open and say why instead of closing as if everything succeeded.
                setServerErrors(bad);
                setServerDuplicates(dupe);
            } else {
                handleClose();
                onSuccess?.();
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data || 'Something went wrong. Please try again.';
            setSubmitError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setLoading(false);
        }
    };

    const hasFeedback = lines.length > 0;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: '20px',
                    overflow: 'hidden',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
                },
            }}
        >
            {/* Orange stripe */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)', flexShrink: 0 }} />

            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddLinkIcon sx={{ color: '#FF5200', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1A1A2E' }}>Add Post Links</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" disabled={loading}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 1 }}>
                <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 0.5 }}>
                    Paste one {platform ? `${displayPlatformName(platform)} ` : 'Instagram, YouTube, X or LinkedIn '}post URL per line.
                    Optionally append a budget amount after the URL.
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#9CA3AF', mb: 1.5, fontFamily: 'monospace' }}>
                    e.g. https://www.instagram.com/reel/... 8000
                </Typography>

                <TextField
                    multiline
                    minRows={5}
                    maxRows={12}
                    fullWidth
                    placeholder={
                        POST_PLACEHOLDERS[platform] || POST_PLACEHOLDERS.Instagram
                    }
                    value={rawText}
                    onChange={e => { setRawText(e.target.value); setServerErrors([]); setServerDuplicates([]); setSubmitError(''); }}
                    disabled={loading}
                    inputRef={textareaRef}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            fontSize: 13,
                            fontFamily: 'monospace',
                            '&.Mui-focused fieldset': { borderColor: '#FF5200' },
                        },
                    }}
                />

                {/* Live validation feedback */}
                {hasFeedback && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 220, overflowY: 'auto' }}>
                        {validLines.map((l, i) => (
                            <Box
                                key={i}
                                onClick={() => jumpToLine(l.lineIndex)}
                                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                                title={`Line ${l.lineNumber} — click to jump to it above`}
                            >
                                <Typography sx={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace', minWidth: 28, mt: 0.2, flexShrink: 0 }}>
                                    #{l.lineNumber}
                                </Typography>
                                {l.isExisting ? (
                                    l.budget != null
                                        ? <Chip label="Budget Update" size="small" sx={{ bgcolor: '#EFF6FF', color: '#2563EB', fontWeight: 600, fontSize: 10.5, height: 20, mt: 0.1, flexShrink: 0 }} />
                                        : <Chip label="Already Added" size="small" sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 600, fontSize: 10.5, height: 20, mt: 0.1, flexShrink: 0 }} />
                                ) : (
                                    <Chip label="Valid" size="small" sx={{ bgcolor: '#D1FAE5', color: '#059669', fontWeight: 600, fontSize: 10.5, height: 20, mt: 0.1, flexShrink: 0 }} />
                                )}
                                <Typography sx={{ fontSize: 12, color: '#374151', wordBreak: 'break-all', flex: 1 }}>
                                    {l.url}
                                    {l.isExisting && l.budget == null && (
                                        <span style={{ color: '#6B7280' }}> — this post is already in the report; add a budget after the URL to update it instead</span>
                                    )}
                                </Typography>
                                {l.budget != null && (
                                    <Chip
                                        label={fmtBudget(l.budget)}
                                        size="small"
                                        sx={{ bgcolor: '#FFF7ED', color: '#C2410C', fontWeight: 700, fontSize: 10.5, height: 20, flexShrink: 0 }}
                                    />
                                )}
                            </Box>
                        ))}
                        {invalidLines.map((l, i) => (
                            <Box
                                key={i}
                                onClick={() => jumpToLine(l.lineIndex)}
                                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                                title={`Line ${l.lineNumber} — click to jump to it above`}
                            >
                                <Typography sx={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace', minWidth: 28, mt: 0.2, flexShrink: 0 }}>
                                    #{l.lineNumber}
                                </Typography>
                                <Chip label="Invalid" size="small" sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600, fontSize: 10.5, height: 20, mt: 0.1, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, color: '#DC2626', wordBreak: 'break-all' }}>
                                    {l.url} — <span style={{ color: '#6B7280' }}>{l.reason}</span>
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Summary row */}
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: overCap ? '#DC2626' : '#6B7280' }}>
                        {newLines.length > 0 && `${newLines.length} new URL${newLines.length !== 1 ? 's' : ''}`}
                        {budgetUpdateLines.length > 0 && `${newLines.length > 0 ? ', ' : ''}${budgetUpdateLines.length} budget update${budgetUpdateLines.length !== 1 ? 's' : ''}`}
                        {alreadyAddedLines.length > 0 && `${newLines.length + budgetUpdateLines.length > 0 ? ', ' : ''}${alreadyAddedLines.length} already added (skipped)`}
                        {invalidLines.length > 0 && `${newLines.length + budgetUpdateLines.length + alreadyAddedLines.length > 0 ? ', ' : ''}${invalidLines.length} invalid`}
                        {lines.length === 0 && ' '}
                    </Typography>
                    {existingPostCount > 0 && newLines.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: overCap ? '#DC2626' : '#6B7280', fontWeight: 600 }}>
                            {totalAfter} / 1500 posts after adding
                        </Typography>
                    )}
                </Box>

                {overCap && (
                    <Alert severity="error" sx={{ mt: 1, borderRadius: '10px', fontSize: 12 }}>
                        Manual reports are capped at 1500 posts. Remove {totalAfter - 1500} URL{totalAfter - 1500 !== 1 ? 's' : ''} to continue.
                    </Alert>
                )}

                {submitError && (
                    <Alert severity="error" sx={{ mt: 1, borderRadius: '10px', fontSize: 12 }}>{submitError}</Alert>
                )}

                {serverErrors.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1, borderRadius: '10px', fontSize: 12 }}>
                        {serverErrors.length} URL{serverErrors.length !== 1 ? 's were' : ' was'} rejected by the server:
                        <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                            {serverErrors.map((u, i) => <li key={i} style={{ wordBreak: 'break-all' }}>{u}</li>)}
                        </Box>
                    </Alert>
                )}

                {serverDuplicates.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1, borderRadius: '10px', fontSize: 12 }}>
                        {serverDuplicates.length} URL{serverDuplicates.length !== 1 ? 's were' : ' was'} already in this report and {serverDuplicates.length !== 1 ? 'were' : 'was'} not added again:
                        <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                            {serverDuplicates.map((d, i) => <li key={i} style={{ wordBreak: 'break-all' }}>{d.link}</li>)}
                        </Box>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ textTransform: 'none', color: '#6B7280', borderRadius: '20px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={loading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : null}
                    variant="contained"
                    sx={{
                        textTransform: 'none',
                        borderRadius: '20px',
                        background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                        boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
                        fontWeight: 600,
                        px: 2.5,
                        '&:hover': { opacity: 0.9 },
                        '&.Mui-disabled': { opacity: 0.5 },
                    }}
                >
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddManualLinksModal;
